import { isRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import editItems from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/edit-availability";
import brandOrderDetails from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/order-fulfilment";
import brandOrderRequestCommissionUpdate from "@faire/web-api--source/routes/brand-portal/orders/brandOrderToken/request-commission-update";
import message from "@faire/web-api--source/routes/messages/otherPartyToken";
import brands from "@faire/web-api--source/routes/support/brands";
import retailers from "@faire/web-api--source/routes/support/retailers";
import retailerOrderDetails from "@faire/web-api--source/routes/user/account/orders/orderToken/brandToken";
import reportItems from "@faire/web-api--source/routes/user/account/orders/orderToken/brandToken/report";
import returnItems from "@faire/web-api--source/routes/user/account/orders/orderToken/brandToken/return";
import { useMemo } from "react";

import { IDropdownOption } from "../ActionModal/types";
import { QuickActionType } from "../types";

import { useActionTypes } from "./useActionTypes";

export const useShowMessage = (
  dropdownOptions: {
    name: string | undefined;
    value: string;
  }[],
  selectedOption: IDropdownOption | undefined
) => {
  const { actionType } = useActionTypes();
  const showEmptyState = !dropdownOptions.length;
  const showNeedExtensionMessage =
    actionType?.id === QuickActionType.REPORT_DAMAGED_OR_MISSING_ITEMS &&
    !!selectedOption?.needExtension;
  const showNotShippedMessage =
    actionType?.id === QuickActionType.TRACK_AN_ORDER &&
    !!selectedOption?.notShipped;
  const showBeingFulfilledMessage =
    isRetailer() &&
    actionType?.id === QuickActionType.CANCEL_AN_ORDER &&
    !!selectedOption?.isBeingFulfilled;

  const redirectLink = useMemo(() => {
    const {
      value: brandOrderToken,
      orderToken,
      brandToken,
      brandOwnerToken,
    } = selectedOption || {};
    if (showEmptyState) {
      return isRetailer() ? retailers() : brands();
    }
    if (isRetailer()) {
      if (!orderToken || !brandToken) {
        return "";
      }
      switch (actionType?.id) {
        case QuickActionType.CANCEL_AN_ORDER:
          return retailerOrderDetails(orderToken, brandToken, {
            cancel: "true",
            brandOrderToken,
          });
        case QuickActionType.TRACK_AN_ORDER:
          return retailerOrderDetails(orderToken, brandToken, {
            tracking: showNotShippedMessage ? undefined : "true",
          });
        case QuickActionType.RETURN_ITEMS:
          return returnItems(orderToken, brandToken);
        case QuickActionType.CONTACT_A_BRAND:
          return message(brandOwnerToken ?? "");
        case QuickActionType.REPORT_DAMAGED_OR_MISSING_ITEMS:
          return showNeedExtensionMessage
            ? retailerOrderDetails(orderToken, brandToken, {
                extend: "true",
                brandOrderToken,
              })
            : reportItems(orderToken, brandToken);
        default:
          return "";
      }
    } else {
      if (!brandOrderToken) {
        return "";
      }
      switch (actionType?.id) {
        case QuickActionType.ACCEPT_AN_ORDER:
          return brandOrderDetails(brandOrderToken);
        case QuickActionType.REQUEST_COMMISSION_CHANGE:
          return brandOrderRequestCommissionUpdate(brandOrderToken);
        case QuickActionType.EDIT_ITEM_AVAILABILITY:
          return editItems(brandOrderToken);
        case QuickActionType.CANCEL_AN_ORDER:
          return brandOrderDetails(brandOrderToken, { cancel: "true" });
        default:
          return "";
      }
    }
  }, [
    actionType,
    selectedOption,
    showEmptyState,
    showNeedExtensionMessage,
    showNotShippedMessage,
  ]);

  return {
    showEmptyState,
    showNeedExtensionMessage,
    showNotShippedMessage,
    showBeingFulfilledMessage,
    redirectLink,
  };
};
